<script setup lang="ts">
import { useAgentClient } from '@/data/Agents/useAgentClient'
import { useAllEntitiesQuery } from '@/data/Agents/useAllEntities'
import { uploadHubFiles } from '@/data/Hubs/uploadHubFiles'
import { useHubClient } from '@/data/Hubs/useHubClient'
import { uploadGoogleDriveURLs } from '@/data/Uploads/uploadGoogleDriveURLs'
import { useEntityClient } from '@/data/useEntityClient'
import CaseMessageInput from '@/modules/Cases/CaseMessageInput.vue'
import { useNewCase } from '@/modules/Cases/useNewCase'
import FileDropzone, {
  type FileAndPath,
} from '@/modules/Workspaces/KnowledgeHub/Common/Dropzone/FileDropzone.vue'
import { useSearchField } from '@/modules/Workspaces/KnowledgeHub/Common/useSearchField'
import ActionBar from '@/modules/Workspaces/KnowledgeHub/Files/ActionBar.vue'
import { provideFileTableContext } from '@/modules/Workspaces/KnowledgeHub/Files/context'
import { provideHubContext } from '@/modules/Workspaces/KnowledgeHub/Hubs/context'
import HubAddDataButton from '@/modules/Workspaces/KnowledgeHub/Hubs/HubAddDataButton.vue'
import HubEmptyState from '@/modules/Workspaces/KnowledgeHub/Hubs/HubEmptyState.vue'
import HubFileTable from '@/modules/Workspaces/KnowledgeHub/Hubs/HubFileTable.vue'
import HubIndexingButton from '@/modules/Workspaces/KnowledgeHub/Hubs/HubIndexingButton.vue'
import { parseHubEntities } from '@/modules/Workspaces/KnowledgeHub/Hubs/parseHubEntities'
import type { HubFile } from '@/modules/Workspaces/KnowledgeHub/Hubs/types'
import type { GoogleDriveFile } from '@/modules/Workspaces/KnowledgeHub/Integrations/GoogleDrive/useGoogleDriveConnection'
import PageContainer from '@/modules/Workspaces/KnowledgeHub/Layout/PageContainer.vue'
import PageTitle from '@/modules/Workspaces/KnowledgeHub/Layout/PageTitle.vue'
import PageTitleBar from '@/modules/Workspaces/KnowledgeHub/Layout/PageTitleBar.vue'
import { provideWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { invariant } from '@/shared/utils/typeAssertions'
import ConfirmationDialog from '@/uiKit/ConfirmationDialog.vue'
import IconButton from '@/uiKit/IconButton.vue'
import { syncRefs, useDropZone } from '@vueuse/core'
import {
  computed,
  ref,
  toRef,
  useTemplateRef,
  watch,
  type ComponentPublicInstance,
  type Ref,
} from 'vue'

const props = defineProps<{ hubId: string; workspaceId: string }>()
const { deleteMutation } = useEntityClient()
const { mutate: deleteEntities } = deleteMutation()

const workspaceId = toRef(props, 'workspaceId')
const hubId = toRef(props, 'hubId')

provideWorkspaceContext(workspaceId)
provideHubContext({ hubId, workspaceId })

const isBulkDeleteConfirmationOpen = ref(false)
const deletionConfirmationText = computed(() => {
  if (selectedFiles.value.size === 1) {
    return 'Are you sure you want to delete this file?'
  } else if (selectedFiles.value.size === hubFiles.value.length) {
    return 'Are you sure you want to delete all files?'
  }
  return `Are you sure you want to delete ${selectedFiles.value.size} files?`
})

const { data: hub } = useHubClient({ workspaceId }).fetch(hubId)
const { data: fileProject } = useAgentClient({ workspaceId }).fetch(() => hub.value?.fileProjectId)
const { data: entities, isFetched: entitiesFetched } = useAllEntitiesQuery({
  workspaceId,
  projectId: () => fileProject.value?.id,
})

const {
  addIntegrationFiles,
  addLibraryFiles,
  addPendingUploads,
  agentId: selectedAgentId,
  hubId: selectedHubId,
  attachments,
  isSendable,
  messageText,
  removeUpload,
  startNewCase,
  webSearch,
} = useNewCase()

syncRefs(toRef(props, 'hubId'), selectedHubId, { immediate: true })

const caseMessageBoxRef = ref<ComponentPublicInstance>()

const { isOverDropZone } = useDropZone(() => caseMessageBoxRef.value?.$el, {
  onDrop: (files) => {
    if (files && files.length > 0) {
      addPendingUploads(files, null)
    }
  },
})

const optimisticUploads: Ref<Map<File, HubFile>> = ref(new Map())

const hubFiles = computed<HubFile[]>(() => {
  if (!fileProject.value || !entities.value) return []

  const parsed = parseHubEntities({ agent: fileProject.value, entities: entities.value })
  return [...optimisticUploads.value.values(), ...parsed]
})

const selectedFiles = ref(new Set<string>())

function onSelectionChange(ids: string[]) {
  selectedFiles.value = new Set(ids)
}

provideFileTableContext({
  filterOwners: ref([]),
  filterFileType: ref([]),
  isSelected(id: string) {
    return selectedFiles.value.has(id)
  },
  setSelected(id: string, state: boolean) {
    if (state) {
      selectedFiles.value.add(id)
    } else {
      selectedFiles.value.delete(id)
    }
  },
})

function onAddFiles(files: File[]) {
  const droppedFiles: FileAndPath[] = files.map((file) => ({
    file,
    fullPath: '/' + (file.webkitRelativePath || file.name),
  }))
  return onDrop(droppedFiles)
}

async function onDrop(droppedFiles: FileAndPath[]) {
  invariant(hub.value)
  invariant(fileProject.value)

  await uploadHubFiles({
    files: droppedFiles,
    hub: hub.value,
    fileProject: fileProject.value,
  })
}

function onMassSelectionChange(selectAll: boolean) {
  if (selectAll) {
    for (const item of hubFiles.value) {
      selectedFiles.value.add(item.entityId)
    }
  } else {
    selectedFiles.value.clear()
  }
}

function onDeleteSelection() {
  isBulkDeleteConfirmationOpen.value = false
  invariant(fileProject.value)
  deleteEntities({
    entityIds: Array.from(selectedFiles.value),
    workspaceId: workspaceId.value,
    projectId: fileProject.value.id,
  })
}

watch(
  () => hubFiles.value,
  () => {
    const nextSelection = new Set<string>()
    for (const item of hubFiles.value) {
      if (selectedFiles.value.has(item.entityId)) {
        nextSelection.add(item.entityId)
      }
    }
    selectedFiles.value = nextSelection
  },
  { immediate: true },
)

function onAddGoogleFiles(files: GoogleDriveFile[]) {
  invariant(fileProject.value)
  invariant(hub.value)

  uploadGoogleDriveURLs({
    files,
    parentEntityId: hub.value.entityId,
    projectId: fileProject.value.id,
    propertyId: 'file',
    workspaceId: fileProject.value.workspaceId,
    getExtraFields: (file) => ({
      /** Hub collections have a `path` field “hardcoded” */
      path: { text: `/${file.name}` },
    }),
  })
}

// ---------------------------------------------------------------------------
// Search field state
// ---------------------------------------------------------------------------

const searchTerm = ref('')
const searchInput = useTemplateRef('searchInput')
const {
  isActive: isSearchActive,
  query: searchQuery,
  eventHandlers: searchFieldHandlers,
} = useSearchField({ focusFn: () => searchInput.value?.focus() })

watch(searchQuery, (term) => (searchTerm.value = term))
</script>

<template>
  <PageContainer
    v-if="hub"
    class="relative flex flex-col pb-3"
  >
    <template v-if="hubFiles.length">
      <PageTitleBar class="mb-10">
        <template #title>
          <div v-if="!isSearchActive">
            <PageTitle>{{ hub?.name }}</PageTitle>
          </div>
          <div
            v-else
            class="flex w-full items-center border-b border-border-input"
          >
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              placeholder="Search for a file…"
              spellcheck="false"
              class="w-full bg-background-transparent text-display-sm-24px-default outline-hidden"
              v-on="searchFieldHandlers"
            />
            <IconButton
              icon="close"
              size="lg"
              wide
              rounded
              variant="outline"
              @click="isSearchActive = false"
            />
          </div>
        </template>
        <template #actions>
          <div
            v-if="!isSearchActive"
            class="flex gap-2"
          >
            <HubIndexingButton :hub-id="hubId" />
            <IconButton
              icon="search"
              size="lg"
              rounded
              variant="outline"
              wide
              aria-label="Search"
              @click="isSearchActive = true"
            />
            <HubAddDataButton
              @import:computer="onAddFiles"
              @import:google="onAddGoogleFiles"
            />
          </div>
        </template>
      </PageTitleBar>
      <FileDropzone
        class="contents"
        @drop="onDrop"
      >
        <HubFileTable
          v-if="entitiesFetched"
          :files="hubFiles"
          :search="searchTerm"
          @selection-change="onSelectionChange"
        />
      </FileDropzone>
    </template>
    <template v-else-if="entitiesFetched">
      <FileDropzone
        class="contents"
        @drop="onDrop"
      >
        <HubEmptyState
          class="flex size-full justify-center"
          @import:computer="onAddFiles"
          @import:google="onAddGoogleFiles"
        />
      </FileDropzone>
    </template>

    <CaseMessageInput
      v-if="entitiesFetched && hubFiles.length"
      ref="caseMessageBoxRef"
      v-model:text="messageText"
      v-model:selected-agent-id="selectedAgentId"
      v-model:selected-hub-id="selectedHubId"
      v-model:web-search="webSearch"
      :attachments="attachments"
      class="mt-5 go-column w-full shrink"
      :locked-hub="true"
      :is-dropping-files="isOverDropZone"
      placeholder="Search, query and interrogate this hub"
      :disabled="!isSendable"
      @submit="
        startNewCase({
          workspaceId: props.workspaceId,
          initialMessage: messageText,
          navigate: 'push',
        })
      "
      @delete-attachment="removeUpload"
      @import:library="addLibraryFiles($event, null)"
      @import:computer="addPendingUploads($event, null)"
      @import:google="addIntegrationFiles($event, null)"
    />

    <Transition
      enter-active-class="transition duration-200 ease-in-out-quint"
      enter-from-class="opacity-0 translate-y-2 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition duration-200 ease-in-out-quint"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-95"
    >
      <ActionBar
        v-if="selectedFiles.size > 0"
        :all="selectedFiles.size === hubFiles.length"
        :selected="selectedFiles.size"
        class="fixed bottom-30 left-1/2 -translate-x-1/2"
        @select="onMassSelectionChange"
        @delete="isBulkDeleteConfirmationOpen = true"
      />
    </Transition>
    <ConfirmationDialog
      :open="isBulkDeleteConfirmationOpen"
      title="Delete files"
      :description="deletionConfirmationText"
      @close="isBulkDeleteConfirmationOpen = false"
      @confirm="onDeleteSelection"
    />
  </PageContainer>
</template>
